import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import ExtensionIcon from "@mui/icons-material/Extension";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import CrossBrowseGridPreview from "./CrossBrowseGridPreview";
import GridBrowseGridPreview from "./GridBrowseGridPreview";
import UtilService from "../../services/UtilService";
import ColorService from "../../services/ColorService";
import CrossCard from "../puzzles/crossword/components/CrossCard";
import GridCard from "../puzzles/grid/components/GridCard";
import SlidingCard from "../browse-sliding/SlidingCard";
import { TagChipAvatar } from "../puzzles/common/components/TagChipAvatar";
import {
    getCrossPlayData,
    getGridPlayData,
    getSlidingPlayData,
    isPlayDataComplete,
    resolvePuzzleCardRawTags,
} from "../puzzles/common/utils/PuzzleCardHelpers";
import {
    buildCrossBrowseGridPreviewFromRow,
    buildGridBrowseGridPreviewFromRow,
    getHomeBrowseStoryLinkedPuzzleLinks,
    getHomeStoryReadApiId,
    getLocalizedHomeBrowseStoryTeaser,
} from "./homePuzzleRoUtils";

/** Fixed teaser slot height — matches `/puzzles/*` browse shelves. */
export const BROWSE_STORY_TEASER_SLOT_HEIGHT_PX = 168;

/** Carousel / library tile width — `/puzzles/hidden-message` and account library use the same values. */
export const BROWSE_SHELF_CARD_WIDTH = {
    xs: "min(92vw, 500px)",
    sm: 440,
    md: 520,
};

/** Crossword / hidden-message browse shelves — compact but readable (~70% of {@link BROWSE_SHELF_CARD_WIDTH}). */
export const BROWSE_COMPACT_SHELF_CARD_WIDTH = {
    xs: "min(92vw, 350px)",
    sm: 308,
    md: 364,
};

/** @deprecated use {@link BROWSE_COMPACT_SHELF_CARD_WIDTH} */
export const BROWSE_CROSSWORD_SHELF_CARD_WIDTH = BROWSE_COMPACT_SHELF_CARD_WIDTH;

/**
 * Grid/Cross card chrome used on `/puzzles/hidden-message` (and sibling browse routes).
 * Pass `storyTeaserRow` as the object that carries nested `story` (browse row or playable puzzle with `template.story`).
 */
export default function BrowseStylePuzzleCard({
    category,
    puzzle,
    storyTeaserRow,
    contentLanguage,
    lang,
    t,
    userPayPlan,
    subscriptionStatus,
    onPlayClick,
    buttonText = "Play",
    isInProgress = false,
    forceUnlock = false,
    hideClusteredBubbles = false,
    primaryActionReplacement = null,
    /** When set, replaces the title-row “read story” link; use for library PDF + story actions above the teaser image. */
    storyTeaserTrailingActions = null,
    actionButtonSx = null,
    teaserIconSx = null,
}) {
    const playData =
        category === "CROSSWORD"
            ? getCrossPlayData(puzzle)
            : category === "SLIDING"
              ? getSlidingPlayData(puzzle)
              : getGridPlayData(puzzle);
    const playComplete = isPlayDataComplete(playData);

    const publishedAt = puzzle?.publishedAt ?? puzzle?.template?.publishedAt ?? null;
    let publishedCaption = null;
    if (publishedAt && !playComplete) {
        const pubMs = new Date(publishedAt).getTime();
        if (Number.isFinite(pubMs) && pubMs > 0) {
            const publishedAgo = UtilService.formatHumanizeDetailed(Math.abs(Date.now() - pubMs), lang);
            if (publishedAgo) {
                publishedCaption = (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0, textAlign: "right" }}
                    >
                        {t("puzzle.card.published")}: {publishedAgo}
                    </Typography>
                );
            }
        }
    }

    const cardDisplayTags = resolvePuzzleCardRawTags(puzzle)
        .filter((tag) => tag && !["bedrock", "story"].includes(String(tag).toLowerCase()));

    const { storyTitle, imageUrl } = getLocalizedHomeBrowseStoryTeaser(
        storyTeaserRow,
        contentLanguage
    );
    const crosswordGridPreview = useMemo(
        () => (category === "CROSSWORD" ? buildCrossBrowseGridPreviewFromRow(storyTeaserRow) : null),
        [category, storyTeaserRow]
    );
    const gridBrowsePreview = useMemo(
        () =>
            category === "HIDDEN_MESSAGE" || category === "WORD_SEARCH"
                ? buildGridBrowseGridPreviewFromRow(storyTeaserRow, category)
                : null,
        [category, storyTeaserRow]
    );
    const storyReadId = getHomeStoryReadApiId(storyTeaserRow);
    const { slidingPlayId, jigsawPlayId } = getHomeBrowseStoryLinkedPuzzleLinks(storyTeaserRow);
    const usesBrowseGridPreview =
        category === "CROSSWORD" || category === "HIDDEN_MESSAGE" || category === "WORD_SEARCH";
    const showCrosswordGrid = category === "CROSSWORD" && crosswordGridPreview != null;
    const showGridBrowsePreview =
        (category === "HIDDEN_MESSAGE" || category === "WORD_SEARCH") && gridBrowsePreview != null;
    const showStoryImage = !usesBrowseGridPreview && Boolean(imageUrl);
    const hasStoryTeaser =
        (!usesBrowseGridPreview && Boolean(storyTitle))
        || showCrosswordGrid
        || showGridBrowsePreview
        || showStoryImage
        || (!usesBrowseGridPreview && Boolean(storyReadId))
        || storyTeaserTrailingActions != null;

    let belowDifficulty = null;
    if (hasStoryTeaser || publishedCaption != null) {
        belowDifficulty = (
            <Box sx={{ width: "100%" }}>
                {hasStoryTeaser ? (
                    <Box sx={{ width: "100%" }}>
                        {/* Grid browse shelves: story title, read-story, sliding, jigsaw links hidden */}
                        {!usesBrowseGridPreview && (storyTitle || storyReadId || storyTeaserTrailingActions != null) ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    width: "100%",
                                    minWidth: 0,
                                    mb: 0.75,
                                }}
                            >
                                {storyTitle ? (
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 700,
                                            lineHeight: 1.35,
                                            flex: "1 1 auto",
                                            minWidth: 0,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {storyTitle}
                                    </Typography>
                                ) : (
                                    <Box sx={{ flex: "1 1 auto", minWidth: 0 }} aria-hidden />
                                )}
                                {storyTeaserTrailingActions != null ? (
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            alignSelf: "flex-start",
                                            mt: -0.5,
                                        }}
                                    >
                                        {storyTeaserTrailingActions}
                                    </Box>
                                ) : storyReadId ? (
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.25,
                                            alignSelf: "flex-start",
                                            mt: -0.5,
                                        }}
                                    >
                                        <Tooltip title={t("puzzles-browse.read-story")}>
                                            <IconButton
                                                component={RouterLink}
                                                to={`/read-story/${encodeURIComponent(storyReadId)}`}
                                                size="small"
                                                aria-label={t("puzzles-browse.read-story")}
                                                sx={teaserIconSx || { color: "#3BA666" }}
                                            >
                                                <AutoStoriesIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {slidingPlayId ? (
                                            <Tooltip
                                                title={t("landing.puzzle-type-sliding")}
                                                arrow
                                            >
                                                <IconButton
                                                    component={RouterLink}
                                                    to={`/puzzle/play-sliding/${encodeURIComponent(slidingPlayId)}`}
                                                    size="small"
                                                    aria-label={t("landing.puzzle-type-sliding")}
                                                    sx={teaserIconSx || { color: "#3BA666" }}
                                                >
                                                    <ViewCarouselIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                        {jigsawPlayId ? (
                                            <Tooltip
                                                title={t("landing.puzzle-type-jigsaw")}
                                                arrow
                                            >
                                                <IconButton
                                                    component={RouterLink}
                                                    to={`/puzzle/play-jigsaw/${encodeURIComponent(jigsawPlayId)}`}
                                                    size="small"
                                                    aria-label={t("landing.puzzle-type-jigsaw")}
                                                    sx={teaserIconSx || { color: "#3BA666" }}
                                                >
                                                    <ExtensionIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </Box>
                                ) : null}
                            </Box>
                        ) : null}
                        {showCrosswordGrid ? (
                            <CrossBrowseGridPreview
                                grid={crosswordGridPreview.grid}
                                filledCoordinates={crosswordGridPreview.filledCoordinates}
                                words={crosswordGridPreview.words}
                            />
                        ) : showGridBrowsePreview ? (
                            <GridBrowseGridPreview
                                grid={gridBrowsePreview.grid}
                                filledCoordinates={gridBrowsePreview.filledCoordinates}
                                gridRows={gridBrowsePreview.gridRows}
                                gridCols={gridBrowsePreview.gridCols}
                                gridSize={gridBrowsePreview.gridSize}
                            />
                        ) : showStoryImage ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: BROWSE_STORY_TEASER_SLOT_HEIGHT_PX,
                                    borderRadius: 1,
                                    bgcolor: "action.hover",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    component="img"
                                    alt=""
                                    src={imageUrl}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: "100%",
                                    ...(usesBrowseGridPreview
                                        ? { aspectRatio: "1" }
                                        : { height: BROWSE_STORY_TEASER_SLOT_HEIGHT_PX }),
                                    borderRadius: 1,
                                    bgcolor: "action.hover",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    boxSizing: "border-box",
                                    flexShrink: 0,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    {usesBrowseGridPreview
                                        ? t("puzzles-browse.no-grid-preview", "No preview")
                                        : t("puzzles-browse.no-story-image", "No image")}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : null}
                {/* Published caption + tags row */}
                {(publishedCaption != null || (category === "SLIDING" && (puzzle.gridSize != null || puzzle.difficultyLevel)) || cardDisplayTags.length > 0) ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 0.75,
                            width: "100%",
                            mt: hasStoryTeaser ? 0.75 : 0,
                        }}
                    >
                        {/* Left: tags + grid size + difficulty chips */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                            {category === "SLIDING" && puzzle.gridSize != null && Number.isFinite(Number(puzzle.gridSize)) && Number(puzzle.gridSize) > 0 && (
                                <Chip
                                    label={`${puzzle.gridSize}×${puzzle.gridSize}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {category === "SLIDING" && puzzle.difficultyLevel && (
                                <Chip
                                    label={
                                        puzzle.difficultyLevel === "EASY"
                                            ? t("puzzle.card.difficulty-easy")
                                            : puzzle.difficultyLevel === "MEDIUM"
                                              ? t("puzzle.card.difficulty-medium")
                                              : puzzle.difficultyLevel === "HARD"
                                                ? t("puzzle.card.difficulty-hard")
                                                : puzzle.difficultyLevel
                                    }
                                    size="small"
                                    sx={{
                                        backgroundColor: ColorService.getDifficultyColor(puzzle.difficultyLevel),
                                        color: "white",
                                        textTransform: "capitalize",
                                    }}
                                />
                            )}
                            {cardDisplayTags.map((tag) => {
                                    const tagKey = `puzzle.tags.${String(tag).toLowerCase()}`;
                                    const fallback = String(tag).charAt(0).toUpperCase() + String(tag).slice(1).toLowerCase();
                                    const translated = t(tagKey);
                                    const displayLabel = translated !== tagKey ? translated : fallback;
                                    return (
                                        <Chip
                                            key={tag}
                                            avatar={<TagChipAvatar tag={tag} size={18} />}
                                            label={displayLabel}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                    );
                                })}
                        </Box>
                        {/* Right: published caption */}
                        {publishedCaption}
                    </Box>
                ) : null}
            </Box>
        );
    }

    const Card =
        category === "CROSSWORD" ? CrossCard : category === "SLIDING" ? SlidingCard : GridCard;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                minWidth: 0,
                overflow: "visible",
            }}
        >
            <Box
                sx={{
                    pt: "10px",
                    flex: "1 1 auto",
                    minHeight: 0,
                    width: "100%",
                    overflow: "visible",
                }}
            >
                <Card
                    puzzle={puzzle}
                    userPayPlan={userPayPlan}
                    subscriptionStatus={subscriptionStatus}
                    onPlayClick={onPlayClick}
                    buttonText={buttonText}
                    isInProgress={isInProgress}
                    forceUnlock={forceUnlock}
                    hideWordCountChip
                    belowDifficultySlot={belowDifficulty}
                    browseStoriesChrome
                    hideClusteredBubbles={hideClusteredBubbles}
                    primaryActionReplacement={primaryActionReplacement}
                    actionButtonSx={actionButtonSx}
                    hideFreeTierBadge={false}
                />
            </Box>
        </Box>
    );
}

BrowseStylePuzzleCard.propTypes = {
    category: PropTypes.oneOf(["CROSSWORD", "WORD_SEARCH", "HIDDEN_MESSAGE", "SLIDING"]).isRequired,
    puzzle: PropTypes.object.isRequired,
    storyTeaserRow: PropTypes.object.isRequired,
    contentLanguage: PropTypes.string,
    lang: PropTypes.string,
    t: PropTypes.func.isRequired,
    userPayPlan: PropTypes.string,
    subscriptionStatus: PropTypes.string,
    onPlayClick: PropTypes.func,
    buttonText: PropTypes.string,
    isInProgress: PropTypes.bool,
    forceUnlock: PropTypes.bool,
    hideClusteredBubbles: PropTypes.bool,
    primaryActionReplacement: PropTypes.node,
    storyTeaserTrailingActions: PropTypes.node,
    actionButtonSx: PropTypes.object,
    teaserIconSx: PropTypes.object,
};
